import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../config';
import authService from '../../utils/authService';

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
  token: string;
}

const PASSWORD_REQUIREMENTS = [
  { label: '16+ kar.', test: (p: string) => p.length >= 16 },
  { label: 'A-Z', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'a-z', test: (p: string) => /[a-z]/.test(p) },
  { label: '0-9', test: (p: string) => /[0-9]/.test(p) },
  { label: 'Sembol', test: (p: string) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(p) },
];

const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({ open, onClose, token }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const requirementsMet = useMemo(() => PASSWORD_REQUIREMENTS.map((req) => req.test(newPassword)), [newPassword]);
  const allRequirementsMet = requirementsMet.every(Boolean);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
  const canSubmit = currentPassword.length > 0 && allRequirementsMet && passwordsMatch && !loading;

  const handleClose = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
    setSuccess(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/api/admin/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Şifre değiştirme başarısız');

      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Password change invalidates all tokens, redirect to login
      if (data.requireRelogin) {
        setTimeout(() => {
          authService.clearTokens();
          navigate('/admin/login');
        }, 1500);
      } else {
        setTimeout(handleClose, 1500);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      color: 'white',
      backgroundColor: 'rgba(255,255,255,0.05)',
      '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
      '&.Mui-focused fieldset': { borderColor: '#90caf9' },
    },
    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#90caf9' },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#252525',
          color: 'white',
          borderRadius: 2,
          minWidth: 320,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>Parola Değiştir</DialogTitle>
      <DialogContent>
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 1,
              py: 0.5,
              backgroundColor: 'rgba(211, 47, 47, 0.15)',
              color: '#f44336',
              '& .MuiAlert-icon': { color: '#f44336' },
            }}
          >
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            sx={{
              mb: 1,
              py: 0.5,
              backgroundColor: 'rgba(46, 125, 50, 0.15)',
              color: '#66bb6a',
              '& .MuiAlert-icon': { color: '#66bb6a' },
            }}
          >
            Başarılı!
          </Alert>
        )}

        <TextField
          label="Mevcut Parola"
          type={showCurrentPassword ? 'text' : 'password'}
          fullWidth
          size="small"
          margin="dense"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          disabled={loading || success}
          sx={textFieldSx}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)} edge="end" size="small" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  {showCurrentPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Yeni Parola"
          type={showNewPassword ? 'text' : 'password'}
          fullWidth
          size="small"
          margin="dense"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          disabled={loading || success}
          sx={textFieldSx}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end" size="small" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Tekrar"
          type={showConfirmPassword ? 'text' : 'password'}
          fullWidth
          size="small"
          margin="dense"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={loading || success}
          error={confirmPassword.length > 0 && !passwordsMatch}
          helperText={confirmPassword.length > 0 && !passwordsMatch ? 'Eşleşmiyor' : ''}
          sx={{ ...textFieldSx, '& .MuiFormHelperText-root': { color: '#f44336', mt: 0.5 } }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" size="small" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ mt: 1.5, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
          {PASSWORD_REQUIREMENTS.map((req, i) => (
            <Typography
              key={i}
              variant="caption"
              sx={{
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
                fontSize: '0.65rem',
                backgroundColor: requirementsMet[i] ? 'rgba(76,175,80,0.2)' : 'rgba(255,255,255,0.1)',
                color: requirementsMet[i] ? '#81c784' : 'rgba(255,255,255,0.5)',
              }}
            >
              {requirementsMet[i] ? <CheckIcon sx={{ fontSize: 10, mr: 0.25, verticalAlign: 'middle' }} /> : null}
              {req.label}
            </Typography>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading} size="small" sx={{ color: 'rgba(255,255,255,0.6)' }}>
          İptal
        </Button>
        <Button onClick={handleSubmit} variant="contained" size="small" disabled={!canSubmit}>
          {loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordDialog;
